import { flatten } from "ramda";
import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Material,
  MeshNormalMaterial,
  MeshPhongMaterial,
  MeshPhysicalMaterial,
  Triangle,
  Vector3,
} from "three";

import { parsePLY, type PLYElement } from "_/data/ply";
import { Uuid } from "_/types";

import { parseSTL } from "./parse";

// A PLY vertex element carrying the coordinates needed to build a mesh. Other
// properties (e.g. color or normals) may also be present, but are unused here.
type MeshVertex = { x: number; y: number; z: number };

const isMeshVertex = (element: PLYElement | undefined): element is MeshVertex =>
  typeof element?.x === "number" &&
  typeof element?.y === "number" &&
  typeof element?.z === "number";

// A PLY face element listing the indices of the vertices it is built from.
type MeshFace = { vertex_indices: number[] };

const isMeshFace = (element: PLYElement | undefined): element is MeshFace =>
  Array.isArray(element?.vertex_indices);

export class Mesh {
  id: Uuid;

  color: Color;

  positions: Array<Vector3>;
  normals: Array<Vector3>;
  center: Vector3;

  bufferGeometry: BufferGeometry | null;
  materials: Record<string, Material>;

  constructor() {
    this.id = crypto.randomUUID();
    this.color = new Color(0x9db0d3);

    this.positions = [];
    this.normals = [];
    this.center = new Vector3(0, 0, 0);

    this.bufferGeometry = null;
    this.materials = {};

    this.makeMaterials();
  }

  private makeMaterials() {
    this.materials = {
      default: new MeshPhongMaterial({ color: this.color }),
      normal: new MeshNormalMaterial(),
      physical: new MeshPhysicalMaterial({
        color: this.color,
        attenuationDistance: 0.1,
        clearcoat: 0.05,
        clearcoatRoughness: 0.05,
        roughness: 0.5,
        reflectivity: 0.1,
      }),
    };
  }

  scale(scalar: number): void {
    this.positions = this.positions.map((p: Vector3) => {
      p.multiplyScalar(scalar);
      return p;
    });
  }

  material(mat = "default"): Material {
    return this.materials[mat] || this.materials.default;
  }

  geometry(): BufferGeometry {
    if (this.bufferGeometry !== null) {
      return this.bufferGeometry;
    }

    const geometry = new BufferGeometry();

    const positions = flatten(this.positions.map((p) => p.toArray()));
    const positionAttribute = new Float32BufferAttribute(positions, 3);

    const normals = flatten(this.normals.map((n) => n.toArray()));
    const normalAttribute = new Float32BufferAttribute(normals, 3);

    geometry.setAttribute("position", positionAttribute);
    geometry.setAttribute("normal", normalAttribute);

    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    if (geometry.boundingBox === null) {
      throw new Error("Unreachable!");
    }

    // Re-position the mesh so that it is centered on the origin.
    const position = geometry.boundingBox.getCenter(new Vector3());
    const inverse = position.negate();
    geometry.translate(...inverse.toArray());

    this.center = geometry.boundingBox.getCenter(this.center);
    this.bufferGeometry = geometry;

    return geometry;
  }

  static fromStl(stlData: ArrayBuffer): Mesh {
    return parseSTL(stlData);
  }

  static fromPly(plyData: ArrayBuffer): Mesh {
    const ply = parsePLY(plyData, true);

    const mesh = new Mesh();

    ply.data.face.forEach((f, i) => {
      if (!isMeshFace(f)) {
        throw new Error(`Face ${i} does not list any vertex indices.`);
      }

      const vertices = f.vertex_indices;

      if (vertices.length !== 3) {
        throw new Error(
          `Meshes must contain only triangles. Face ${i} contains ${vertices.length} vertices.`
        );
      }

      const facet: Vector3[] = [];

      for (let j = 0; j < 3; j++) {
        const vertex = ply.data.vertex[vertices[j]];

        if (!isMeshVertex(vertex)) {
          throw new Error(
            `Face ${i} references vertex ${vertices[j]}, which has no x/y/z coordinates.`
          );
        }

        const point = new Vector3(vertex.x, vertex.y, vertex.z);

        facet.push(point);
      }

      mesh.addFacet(facet);
    });

    return mesh;
  }

  addFacet(facet: Vector3[]): void {
    const n = new Triangle(...facet).getNormal(new Vector3());

    for (let i = 0; i < 3; i++) {
      this.positions.push(facet[i]);
      this.normals.push(n);
    }
  }
}
